import type { GeneralCommentLocation } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import type { InlineCommentLocation } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery';
import type {
	Comment,
	CommentReply,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';

import type {
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment as UnreadCommentNode,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment_location_InlineComment as UnreadLocationInlineComment,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment_replies_Comment as UnreadReplyCommentNode,
} from '../__types__/UnreadCommentCountUnreadCommentQuery';
import type { UnreadCommentIDSetOperation } from '../types/unreadCommentsTypes';

export interface CreateUnreadCommentListProps {
	allComments: (UnreadCommentNode | Comment | null)[] | null | undefined;
	currentUserId: string | null;
	pageId?: string;
	updateUnreadCommentIDSet: (
		unreadCommentIDSet: Set<string>,
		operation: UnreadCommentIDSetOperation,
	) => void;
	resetStates: () => void;
	viewedComments: Set<string>;
	isDanglingCallback?: (markerRef: string | null | undefined) => boolean;
	addNewCommentThreads?: (commentsDataMap: any) => void;
}

// Minimal comment type containing only fields needed for validation
export type CommentForValidation = {
	id: string;
	author: {
		accountId?: string | null;
	};
	createdAt: {
		value: string;
	};
	location: {
		type: 'INLINE' | 'FOOTER';
		inlineMarkerRef?: string;
		inlineResolveProperties?: {
			resolved?: boolean;
		};
		commentResolveProperties?: {
			resolved?: boolean;
		};
	};
};

type ValidUnreadComment = {
	comment: CommentForValidation;
	currentUserId: string;
	viewedComments: Set<string>;
	isDanglingCallback?: (markerRef: string | null | undefined) => boolean;
};

export const buildValidationObject = (
	comment: NonNullable<UnreadCommentNode | UnreadReplyCommentNode | Comment | CommentReply>,
): CommentForValidation => {
	return {
		id: comment.id,
		author: {
			accountId: comment.author?.__typename === 'KnownUser' ? comment.author.accountId : undefined,
		},
		createdAt: {
			value: comment.createdAt?.value || '',
		},
		location: {
			type: comment.location.type as 'INLINE' | 'FOOTER',
			inlineMarkerRef: (comment.location as any)?.inlineMarkerRef,
			inlineResolveProperties: (comment.location as any)?.inlineResolveProperties,
			commentResolveProperties: (comment.location as any)?.commentResolveProperties,
		},
	};
};

// Overload for building validation object from individual props
export const buildValidationObjectFromProps = ({
	commentId,
	userId,
	createdAt,
	commentType,
	resolveProperties,
}: {
	commentId: string;
	userId?: string | null;
	createdAt?: string;
	commentType: 'INLINE' | 'FOOTER';
	resolveProperties?: {
		resolved?: boolean;
	};
}): CommentForValidation => {
	return {
		id: commentId,
		author: {
			accountId: userId || undefined,
		},
		createdAt: {
			value: createdAt || '',
		},
		location: {
			type: commentType,
			...(commentType === 'INLINE'
				? {
						inlineResolveProperties: resolveProperties || { resolved: false },
					}
				: {
						commentResolveProperties: resolveProperties || { resolved: false },
					}),
		},
	};
};

export const commentIsDangling =
	({
		pageMode = 'view',
		annotationsInEditorDoc,
		rendererAnnotations,
	}: {
		pageMode: 'view' | 'edit' | 'live';
		annotationsInEditorDoc: Set<string>;
		rendererAnnotations: Set<string>;
	}) =>
	(markerRef: string | null | undefined): boolean => {
		if (!markerRef) {
			return false; // default to false if no marker ref
		}

		// Treat 'live' mode as 'edit' mode
		const effectivePageMode = pageMode === 'live' ? 'edit' : pageMode;

		return effectivePageMode === 'view'
			? !rendererAnnotations.has(markerRef)
			: !annotationsInEditorDoc.has(markerRef);
	};

export const isValidUnreadComment = ({
	comment,
	currentUserId,
	viewedComments,
	isDanglingCallback,
}: ValidUnreadComment) => {
	// Early return if comment is already viewed
	if (viewedComments.has(comment.id)) {
		return false;
	}

	const isNotOwnComment = comment.author?.accountId && comment.author.accountId !== currentUserId;
	const createdLast30Days =
		new Date(comment.createdAt?.value) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

	const commentLocation = comment?.location as UnreadLocationInlineComment;
	const markerRef = commentLocation?.inlineMarkerRef;

	const isDanglingInlineComment = isDanglingCallback ? isDanglingCallback(markerRef) : false;
	const isPageComment = comment?.location.type === 'FOOTER';

	const isResolved =
		(comment?.location as InlineCommentLocation)?.inlineResolveProperties?.resolved ||
		(comment?.location as GeneralCommentLocation)?.commentResolveProperties?.resolved;

	return (
		!isResolved &&
		isNotOwnComment &&
		createdLast30Days &&
		(!isDanglingInlineComment || isPageComment)
	);
};

export const createUnreadCommentsSet = async ({
	allComments,
	currentUserId,
	pageId,
	updateUnreadCommentIDSet,
	resetStates,
	viewedComments,
	isDanglingCallback,
	addNewCommentThreads,
}: CreateUnreadCommentListProps) => {
	if (!allComments || !pageId) {
		return;
	}

	const unreadCommentIds: Set<string> = new Set();

	// keep track inline comment threads so we can prepopulate comments data map in order to keep track of dangling and resolved comments for unread
	const inlineCommentThreadsToAdd: Record<string, any> = {};

	allComments.forEach((comment: UnreadCommentNode | Comment | null) => {
		if (!comment) {
			return;
		}

		if (comment.location.type === 'INLINE') {
			const commentLocation = comment.location as UnreadLocationInlineComment;
			const threadKey = commentLocation?.inlineMarkerRef;
			if (threadKey) {
				// Our query only returns open comments, so we need to set the isOpen status to true
				inlineCommentThreadsToAdd[threadKey] = {
					...comment,
					isOpen: true,
					wasRemovedByAnotherUser: false,
				};
			}
		}

		if (currentUserId) {
			const parentCommentForValidation = buildValidationObject(comment);

			if (
				isValidUnreadComment({
					comment: parentCommentForValidation,
					currentUserId,
					viewedComments,
					isDanglingCallback,
				})
			) {
				unreadCommentIds.add(comment.id);
			}

			if (comment.replies) {
				comment.replies.forEach((reply) => {
					if (reply) {
						const replyForValidation = buildValidationObject(reply);

						if (
							isValidUnreadComment({
								comment: replyForValidation,
								currentUserId,
								viewedComments,
								isDanglingCallback,
							})
						) {
							unreadCommentIds.add(reply.id);
						}
					}
				});
			}
		}
	});

	updateUnreadCommentIDSet(unreadCommentIds, 'initialize');

	// Add the comment threads for inline comments at the end
	addNewCommentThreads &&
		addNewCommentThreads({
			inline: inlineCommentThreadsToAdd,
			general: {},
		});

	// Once we are done updating the unread list, need to reset the states.
	resetStates();
};
